/**
 * Maps country names to BCP 47 locale codes for date formatting
 * Uses browser locale detection when country is not mapped
 */

// Mapping of country names to locale codes
// Based on common date format preferences and language
const COUNTRY_TO_LOCALE_MAP: Record<string, string> = {
  // North America - MM/DD/YYYY format
  "United States": "en-US",
  "Canada": "en-CA",
  "Mexico": "es-MX",
  
  // Europe - DD/MM/YYYY format (mostly)
  "United Kingdom": "en-GB",
  "Ireland": "en-IE",
  "France": "fr-FR",
  "Germany": "de-DE",
  "Italy": "it-IT",
  "Spain": "es-ES",
  "Portugal": "pt-PT",
  "Netherlands": "nl-NL",
  "Belgium": "nl-BE",
  "Switzerland": "de-CH",
  "Austria": "de-AT",
  "Sweden": "sv-SE",
  "Norway": "nb-NO",
  "Denmark": "da-DK",
  "Finland": "fi-FI",
  "Poland": "pl-PL",
  "Czech Republic": "cs-CZ",
  "Czechia": "cs-CZ",
  "Greece": "el-GR",
  "Hungary": "hu-HU",
  "Romania": "ro-RO",
  "Bulgaria": "bg-BG",
  "Croatia": "hr-HR",
  "Slovakia": "sk-SK",
  "Slovenia": "sl-SI",
  "Estonia": "et-EE",
  "Latvia": "lv-LV",
  "Lithuania": "lt-LT",
  "Iceland": "is-IS",
  "Luxembourg": "fr-LU",
  "Malta": "mt-MT",
  "Cyprus": "el-CY",
  
  // Middle East
  "Israel": "en-GB", // Use DD/MM/YYYY format (he-IL uses Hebrew numerals which may format differently)
  "Saudi Arabia": "ar-SA",
  "United Arab Emirates": "ar-AE",
  "Jordan": "ar-JO",
  "Lebanon": "ar-LB",
  "Iran": "fa-IR",
  "Iraq": "ar-IQ",
  "Turkey": "tr-TR",
  "Egypt": "ar-EG",
  
  // Asia - YYYY-MM-DD or DD/MM/YYYY
  "China": "zh-CN",
  "Japan": "ja-JP",
  "South Korea": "ko-KR",
  "India": "en-IN",
  "Indonesia": "id-ID",
  "Thailand": "th-TH",
  "Vietnam": "vi-VN",
  "Singapore": "en-SG",
  "Malaysia": "ms-MY",
  "Philippines": "en-PH",
  "Taiwan": "zh-TW",
  "Hong Kong": "zh-HK",
  "Bangladesh": "bn-BD",
  "Pakistan": "ur-PK",
  "Sri Lanka": "si-LK",
  
  // Oceania
  "Australia": "en-AU",
  "New Zealand": "en-NZ",
  
  // South America - DD/MM/YYYY
  "Brazil": "pt-BR",
  "Argentina": "es-AR",
  "Chile": "es-CL",
  "Colombia": "es-CO",
  "Peru": "es-PE",
  "Venezuela": "es-VE",
  "Ecuador": "es-EC",
  "Uruguay": "es-UY",
  "Paraguay": "es-PY",
  "Bolivia": "es-BO",
  
  // Africa
  "South Africa": "en-ZA",
  "Nigeria": "en-NG",
  "Kenya": "en-KE",
  "Ghana": "en-GH",
  "Morocco": "ar-MA",
  "Tunisia": "ar-TN",
  "Algeria": "ar-DZ",
  
  // Other
  "Russia": "ru-RU",
  "Ukraine": "uk-UA",
  "Belarus": "be-BY",
  "Kazakhstan": "kk-KZ",
};

/**
 * Converts a country name to a locale code
 * @param country - Country name (e.g., "United States", "United Kingdom")
 * @returns BCP 47 locale code (e.g., "en-US", "en-GB") or undefined if not found
 */
export function countryToLocale(country: string | undefined | null): string | undefined {
  if (!country) {
    return undefined;
  }
  
  // Normalize country name (trim, handle variations)
  const normalizedCountry = country.trim();
  
  // Check direct mapping
  if (COUNTRY_TO_LOCALE_MAP[normalizedCountry]) {
    return COUNTRY_TO_LOCALE_MAP[normalizedCountry];
  }
  
  // Try case-insensitive lookup
  const lowerCountry = normalizedCountry.toLowerCase();
  for (const [key, value] of Object.entries(COUNTRY_TO_LOCALE_MAP)) {
    if (key.toLowerCase() === lowerCountry) {
      return value;
    }
  }
  
  // Handle variations and aliases
  const variations: Record<string, string> = {
    "usa": "en-US",
    "us": "en-US",
    "uk": "en-GB",
    "u.k.": "en-GB",
    "u.s.": "en-US",
    "u.s.a.": "en-US",
  };
  
  if (variations[lowerCountry]) {
    return variations[lowerCountry];
  }
  
  return undefined;
}

/**
 * Gets the locale to use for date formatting
 * @param country - Optional country name from user profile
 * @returns BCP 47 locale code, defaults to browser locale if country not found
 */
export function getLocaleForDate(country?: string | null): string {
  const locale = countryToLocale(country);
  
  if (locale) {
    return locale;
  }
  
  // Fallback to browser locale
  return navigator.language || "en-US";
}

